<script setup lang="ts">
import { computed } from 'vue';
import {
  ContractGenerationAnswerOperator,
  ContractGenerationQuestionType,
  type ContractGenerationTemplateQuestionInDb,
} from '@/api';
import { isEnumValue } from '@/utils/enumUtils';
import {
  ANSWER_OPERATORS,
  answerDraftError,
  blankAnswerDraft,
  describeAnswerDraft,
  knownValues,
  operatorLabel,
  takesSecondOperand,
  type VariantAnswerDraft,
  type VariantAnswerDraftMap,
} from '@/utils/contractGeneration/questions';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaDateField from '@/components/design/base/inputs/GaiaDateField.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';

/**
 * What one document answers: the word it serves, or a comparison — an operator
 * and one or two operands.
 *
 * Shared by the two places answers are set — the editor on a document that
 * exists, and the dialog that adds one — so a comparison is entered and read
 * back the same way in both.
 *
 * A choice is **typed here and picked at drafting time**, which is the only way
 * round that works: the picker's contents are the distinct answers the
 * documents have given, so it cannot also be how they give them.
 *
 * Nothing here is demanded. Leaving a question blank is a real answer and the
 * widening one: it doesn't narrow the document, so it stays a candidate
 * whatever is picked.
 */
interface Props {
  questions: ContractGenerationTemplateQuestionInDb[];
  draft: VariantAnswerDraftMap;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), { disabled: false });

const emit = defineEmits<{
  (e: 'update:draft', value: VariantAnswerDraftMap): void;
}>();

/** Whether any row is being given a band — the only operator with a boundary. */
const hasBetween = computed<boolean>(() =>
  props.questions.some(question => takesSecondOperand(rowFor(question.uid).operator))
);

function rowFor(questionUid: string): VariantAnswerDraft {
  return props.draft[questionUid] ?? blankAnswerDraft();
}

function patch(questionUid: string, changes: Partial<VariantAnswerDraft>): void {
  emit('update:draft', {
    ...props.draft,
    [questionUid]: { ...rowFor(questionUid), ...changes },
  });
}

function operatorOptions(question: ContractGenerationTemplateQuestionInDb): {
  label: string;
  value: string;
}[] {
  return ANSWER_OPERATORS.map(operator => ({
    label: operatorLabel(question.question_type, operator),
    value: operator,
  }));
}

/** Dropping `between` for another operator strands the second operand, which
 *  the backend refuses rather than ignores — so it is cleared with the switch. */
function chooseOperator(question: ContractGenerationTemplateQuestionInDb, raw: string): void {
  if (!isEnumValue(ContractGenerationAnswerOperator, raw)) return;
  patch(question.uid, {
    operator: raw,
    ...(takesSecondOperand(raw) ? {} : { numberValueMax: '', dateValueMax: null }),
  });
}
</script>

<template>
  <n-flex vertical :size="16">
    <!-- Shown once a band is actually being written, not before: every other
         operator is closed and needs no explaining, and a paragraph about
         boundaries above an untouched form is only wall. -->
    <gaia-text v-if="hasBetween" size="meta" tone="subtle">
      <strong>Between</strong> is half-open — the first value counts, the second doesn’t. So
      “between 0 and under 50,000” and “at least 50,000” meet exactly once, with 50,000 in the upper
      one.
    </gaia-text>

    <gaia-form-field
      v-for="question in props.questions"
      :key="question.uid"
      :label="question.label"
      :required="question.required_for_variants"
      :hint="question.hint ?? undefined"
      :error="answerDraftError(question, rowFor(question.uid)) ?? undefined"
    >
      <!-- A choice: typed, not picked. The list of answers is built *from*
           these, so offering it back here would be circular — and the first
           document to serve a new word could never enter it. The words already
           in use are shown underneath instead, so spellings stay together. -->
      <n-flex
        v-if="question.question_type === ContractGenerationQuestionType.SELECTION"
        vertical
        :size="5"
      >
        <!-- Clearable because emptying it is a real answer: a document that
             says nothing here serves any value, and that has to be reachable
             without retyping over the old word. -->
        <gaia-input
          :value="rowFor(question.uid).value"
          :placeholder="`Any ${question.label.toLowerCase()}`"
          :disabled="props.disabled"
          clearable
          style="width: 100%"
          @update:value="(value: string) => patch(question.uid, { value })"
          @clear="patch(question.uid, { value: '' })"
        />
        <gaia-text v-if="knownValues(question).length > 0" size="meta" tone="subtle">
          Other documents answer: {{ knownValues(question).join(', ') }}
        </gaia-text>
      </n-flex>

      <!-- A comparison: how this document decides whether a typed value is
           one it serves. Aligned to the top because the date field grows a
           hint under itself while it is being typed in. -->
      <n-flex v-else vertical :size="5">
        <n-flex align="flex-start" :size="8" :wrap="false">
          <gaia-select
            :value="rowFor(question.uid).operator"
            :options="operatorOptions(question)"
            placeholder="Compares…"
            :disabled="props.disabled"
            :style="{ flex: '0 0 148px' }"
            @update:value="(value: string) => chooseOperator(question, value)"
          />

          <template v-if="question.question_type === ContractGenerationQuestionType.NUMBER">
            <gaia-input
              :value="rowFor(question.uid).numberValue"
              placeholder="Value"
              :disabled="props.disabled"
              :style="{ flex: '1 1 0', minWidth: 0 }"
              @update:value="(value: string) => patch(question.uid, { numberValue: value })"
            />
            <template v-if="takesSecondOperand(rowFor(question.uid).operator)">
              <gaia-text
                size="meta"
                tone="subtle"
                :style="{ flex: 'none', whiteSpace: 'nowrap', paddingTop: '9px' }"
              >
                and under
              </gaia-text>
              <gaia-input
                :value="rowFor(question.uid).numberValueMax"
                placeholder="Value"
                :disabled="props.disabled"
                :style="{ flex: '1 1 0', minWidth: 0 }"
                @update:value="(value: string) => patch(question.uid, { numberValueMax: value })"
              />
            </template>
          </template>

          <template v-else>
            <gaia-date-field
              :value="rowFor(question.uid).dateValue"
              placeholder="Date"
              :disabled="props.disabled"
              :style="{ flex: '1 1 0', minWidth: 0 }"
              @update:value="(value: string | null) => patch(question.uid, { dateValue: value })"
            />
            <template v-if="takesSecondOperand(rowFor(question.uid).operator)">
              <gaia-text
                size="meta"
                tone="subtle"
                :style="{ flex: 'none', whiteSpace: 'nowrap', paddingTop: '9px' }"
              >
                and before
              </gaia-text>
              <gaia-date-field
                :value="rowFor(question.uid).dateValueMax"
                placeholder="Date"
                :disabled="props.disabled"
                :style="{ flex: '1 1 0', minWidth: 0 }"
                @update:value="
                  (value: string | null) => patch(question.uid, { dateValueMax: value })
                "
              />
            </template>
          </template>
        </n-flex>

        <!-- The interpretation, in the words the cards will use. It earns its
             place most on "between", whose upper operand is excluded. -->
        <gaia-text
          v-if="describeAnswerDraft(question, rowFor(question.uid))"
          size="meta"
          tone="subtle"
        >
          Serves {{ describeAnswerDraft(question, rowFor(question.uid)) }}
        </gaia-text>
      </n-flex>
    </gaia-form-field>
  </n-flex>
</template>
